import { Status } from '@atlassian/browser-storage-controls/controllers/cookie-controls/set-cookie/types';
import { setCookie, getCookie, deleteCookie, setStrictlyNecessaryCookie, initializeControls } from '@atlassian/browser-storage-controls/cookie-controls';
import type { JSCookieAttributes } from '@atlassian/browser-storage-controls/controllers/cookie-controls/set-cookie';
import { checkThirdParty } from '@atlassian/browser-storage-controls';
// eslint-disable-next-line no-restricted-imports
import { isFedRamp, isIsolatedCloud } from '@atlassian/atl-context';
import type { BscInitializationResult } from '@atlassian/browser-storage-controls/types';

export enum CookieLevel {
	USE_PREFERENCES = 'USE_PREFERENCES',
	NECESSARY = 'NECESSARY',
}

// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
// to the customer test checks (which previously also matched internal test traffic including hello).
let hasSetAnalyticsCookie = false;

export class Cookies {
	/**
	 * @deprecated Use `initializeBrowserStorageControls` from `@confluence/browser-storage-controls` instead
	 * when the `confluence_browser_storage_controls` feature gate is enabled.
	 */
	static initialize({
		userId,
		cloudId,
	}: {
		userId?: string;
		cloudId?: string;
	}): Promise<BscInitializationResult | undefined> {
		// we want to store cookies locally when in Isolated Cloud (IC) because like FedRAMP, Consent Hub is not hydrated in IC
		// (note: this property is ignored and forced to "true" when a FedRAMP perimeter is passed in)
		const shouldUseLocalConsentMode = isIsolatedCloud();

		return initializeControls({
			product: 'Confluence',
			perimeter: isFedRamp() ? 'fedramp-moderate' : 'commercial',
			localConsentMode: shouldUseLocalConsentMode,
			userId,
			cloudId,
		});
	}

	// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
	// to the customer test checks (which previously also matched internal test traffic including hello).
	static hasAnalyticsCookieBeenSet(): boolean {
		return hasSetAnalyticsCookie;
	}

	/**
	 * Attempts to set a cookie asynchronously using the user's preferences
	 * @param level Describes how necessary the cookie is. Only use NECESSARY if the cookie is absolutely required.
	 * @returns true if the cookie succeeded in being set based on the user's preferences
	 */
	static async set(
		key: string,
		value: string,
		attributes?: JSCookieAttributes,
		level: CookieLevel = CookieLevel.USE_PREFERENCES,
	): Promise<boolean> {
		// This is required for HOT-302777 to determine if the analytics cookie has been set by code unrelated
		// to the customer test checks (which previously also matched internal test traffic including hello).
		if (key === 'atlassian_analytics_debug') {
			hasSetAnalyticsCookie = true;
		}

		// This does not need to be behind the FF because
		// if the cookie is strictly necessary, its set synchronously using js-cookie anyways
		if (level === CookieLevel.NECESSARY) {
			setStrictlyNecessaryCookie(key, value, attributes);
			return Promise.resolve(true);
		}

		return (await setCookie(key, value, attributes)) === Status.SUCCESS;
	}

	/**
	 * Retrieves a single cookie or all cookies if no key is provided
	 */
	// eslint-disable-next-line no-dupe-class-members
	static get(): { [key: string]: string };
	// eslint-disable-next-line no-dupe-class-members
	static get(key: string): string | undefined;
	// eslint-disable-next-line no-dupe-class-members
	static get(key?: string) {
		if (!key) {
			return getCookie();
		}
		return getCookie(key);
	}

	/**
	 * Deletes a cookie given a key
	 */
	static remove(key: string, attributes?: JSCookieAttributes): void {
		deleteCookie(key, attributes);
	}

	/**
	 * Executes a callback if the user has allowed or denied a third party cookie
	 */
	static async checkThirdParty(
		key: string,
		onAllowed: () => Promise<void>,
		onDenied?: () => Promise<void>,
	): Promise<void> {
		await checkThirdParty(key, onAllowed, onDenied);
	}
}
